import React, { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { ArrowLeft, Camera, Feather, Check } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import WinPhotos from "@/components/WinPhotos";

/**
 * Becoming Gallery — the "Capture · Honor" experience: a private view of the
 * member's own Becoming photos, arranged newest first. Focus is on honoring
 * the arc, not on building a social-media feed. Nothing here is broadcast;
 * these are the moments the member chose to preserve for themselves.
 */
export default function BecomingGallery({ onBack }) {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await endpoints.becoming();
        if (!cancelled) setPosts(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) { setPosts([]); setError(true); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-10" data-testid="becoming-gallery">
      <button
        type="button"
        onClick={onBack}
        data-testid="becoming-back"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--clay))] tab-link"
      >
        <ArrowLeft size={12} /> Back to Profile
      </button>

      <header className="reveal">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
          <Camera size={12} /> Capture · Honor
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-3 italic">
          The moments you honor.
        </h1>
        <p className="mt-4 max-w-2xl text-[hsl(var(--muted-foreground))] leading-relaxed">
          A private record of your Becoming — kept for you first. These moments stay yours unless you intentionally
          choose to share one. No feed, no ranking, no audience. Just the arc.
        </p>
      </header>

      {posts === null && (
        <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="becoming-loading">
          Gathering the frames…
        </div>
      )}

      {posts && posts.length === 0 && (
        <EmptyState errored={error} />
      )}

      {posts && posts.length > 0 && (
        <div
          className="grid gap-8 sm:grid-cols-2"
          data-testid="becoming-grid"
        >
          {posts.map((p, i) => (
            <BecomingCard
              key={p.id}
              post={p}
              index={i}
              onNoteSaved={(id, note) => {
                setPosts((prev) => (prev || []).map((x) => x.id === id ? { ...x, becoming_note: note } : x));
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BecomingCard({ post, index, onNoteSaved }) {
  const when = formatWhen(post.created_at);
  const [note, setNote] = useState(post.becoming_note || "");
  const [saving, setSaving] = useState(false);
  const initial = post.becoming_note || "";
  const dirty = note.trim() !== initial.trim();

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const { data } = await endpoints.updateBecomingNote(post.id, note.trim());
      const saved = (data && data.becoming_note) || note.trim();
      onNoteSaved?.(post.id, saved);
      toast.success("Note held.");
    } catch {
      toast.error("Couldn't save the note yet.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <article
      className="p-4 border hairline rounded-md bg-[hsl(var(--paper))] reveal"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
      data-testid={`becoming-card-${post.id}`}
    >
      <WinPhotos post={post} size="md" testId={`becoming-photos-${post.id}`} />
      <div className="mt-4 flex items-baseline justify-between gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
          {when}
        </div>
        {post.circle && (
          <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--clay))]">
            {post.circle}
          </div>
        )}
      </div>
      {post.content && (
        <p className="mt-2 text-sm text-[hsl(var(--fg))] leading-relaxed">
          {post.content}
        </p>
      )}

      <div className="mt-4 pt-4 border-t hairline" data-testid={`becoming-note-block-${post.id}`}>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
          <Feather size={11} /> Your reflection
        </div>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid={`becoming-note-input-${post.id}`}
          rows={2}
          placeholder="A quiet line for your future self — what were you holding this day?"
          className="mt-2 bg-transparent border-[hsl(var(--border))] text-sm focus-visible:ring-[hsl(var(--terracotta))]"
        />
        <div className="mt-2 flex items-center justify-end gap-3">
          {!dirty && initial && (
            <span
              className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--moss))] inline-flex items-center gap-1"
              data-testid={`becoming-note-status-${post.id}`}
            >
              <Check size={11} /> Held
            </span>
          )}
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            data-testid={`becoming-note-save-${post.id}`}
            className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border hairline tab-link ${
              dirty
                ? "bg-[hsl(var(--terracotta))] text-[hsl(var(--paper))] border-transparent hover:bg-[hsl(var(--terracotta))]/90"
                : "text-[hsl(var(--muted-foreground))]/60 cursor-not-allowed"
            }`}
          >
            {saving ? "Holding…" : dirty ? "Save note" : "Saved"}
          </button>
        </div>
      </div>
    </article>
  );
}

function EmptyState({ errored }) {
  return (
    <div
      className="p-10 border hairline rounded-md bg-[hsl(var(--paper))] text-center"
      data-testid="becoming-empty"
    >
      <div className="font-display text-2xl">
        {errored ? "The gallery is quiet right now." : "No moments captured yet."}
      </div>
      <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
        Share a Becoming check-in on the Home page with both a Then and a Now
        image and it will land here — held for you, and only you.
      </p>
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch { return ""; }
}
